import React, { useState, useEffect } from 'react';
import { generateThumbnailBlob } from '../../utils/thumbnailGenerator';
import { API } from '../../services/api';
import config from '../../config';
import './SignatureThumbnail.css';

function SignatureThumbnail({
    signatureId,
    uid,
    signatureData = null,
    updatedAt,
    containerWidth = 600,
    width = 300,
    height = 200,
    onClick
}) {
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (signatureId && uid) {
            generateThumbnail();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signatureId, uid, updatedAt]);

    const generateThumbnail = async () => {
        setLoading(true);
        setError(false);

        try {
            // 1. Essayer de charger depuis le backend (priorité)
            try {
                const backendUrl = config.getThumbnailUrl(uid);
                const response = await fetch(backendUrl, {
                    cache: 'no-cache'
                });

                if (response.ok) {
                    // Le thumbnail existe sur le backend - l'utiliser directement
                    const blob = await response.blob();
                    const dataUrl = await new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.readAsDataURL(blob);
                    });

                    console.log('[SignatureThumbnail] Loaded from backend');
                    setThumbnailUrl(dataUrl);
                    setLoading(false);
                    return;
                }
            } catch (backendError) {
                console.log('[SignatureThumbnail] Backend thumbnail not found, generating client-side...');
            }

            // 2. Fallback : Générer côté client avec la fonction partagée
            let gridData = signatureData?.grid_data;
            let sigContainerWidth = signatureData?.container_width || containerWidth;

            if (!gridData) {
                const response = await API.getSignature(signatureId);
                if (!response || !response.data) {
                    throw new Error('Signature non trouvée');
                }
                gridData = response.data.signature.grid_data;
                sigContainerWidth = response.data.signature.container_width || containerWidth;
            }

            // Utiliser la fonction partagée qui gère correctement les images
            console.log('[SignatureThumbnail] Generating with shared function...');
            const thumbnailBlob = await generateThumbnailBlob(gridData, sigContainerWidth);

            // Convertir le blob en data URL pour l'affichage
            const dataUrl = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(thumbnailBlob);
            });

            // Uploader au backend pour les prochains chargements
            try {
                await API.uploadThumbnail(signatureId, thumbnailBlob);
                console.log('[SignatureThumbnail] Thumbnail uploaded to backend');
            } catch (uploadError) {
                console.warn('[SignatureThumbnail] Failed to upload thumbnail:', uploadError);
            }

            setThumbnailUrl(dataUrl);
            setLoading(false);

        } catch (err) {
            console.error('[SignatureThumbnail] Error:', err);
            setError(true);
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div
                className="signature-thumbnail loading"
                style={{ width: `${width}px`, height: `${height}px` }}
                onClick={onClick}
            >
                <div className="thumbnail-spinner"></div>
                <span>Génération...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div
                className="signature-thumbnail error"
                style={{ width: `${width}px`, height: `${height}px` }}
                onClick={onClick}
            >
                <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Aperçu non disponible</span>
            </div>
        );
    }

    return (
        <div
            className="signature-thumbnail"
            style={{ width: `${width}px`, height: `${height}px` }}
            onClick={onClick}
        >
            <img
                src={thumbnailUrl}
                alt="Aperçu de la signature"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
        </div>
    );
}

export default SignatureThumbnail;
